import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const LINK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/link`;
const LINK_ERR = { ms_mismatch: "Entra con lo stesso account Microsoft della tua email in ON! Suite.", unauthorized: "Questo account Microsoft non può essere collegato all'azienda.", disabled: "Account disabilitato." };

export const MicrosoftAccessPanel = () => {
  const [s, setS] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => { api.get("/admin/microsoft-access").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (params.get("ms") === "ok") { toast.success("Microsoft 365 collegato: ora l'azienda entra con Microsoft"); }
    if (params.get("ms_error")) { toast.error(LINK_ERR[params.get("ms_error")] || "Collegamento Microsoft non riuscito."); }
    if (params.get("ms") || params.get("ms_error")) { setParams({}, { replace: true }); }
  }, [params, setParams]);
  if (!s) return null;
  return (
    <Panel className="p-5" data-testid="ms-access-panel">
      <SectionLabel>Accesso con Microsoft 365</SectionLabel>
      {!s.available ? <p className="mt-2 text-xs text-slate-500" data-testid="ms-access-unavailable">Non ancora disponibile: verrà attivato dalla piattaforma.</p>
        : s.linked ? <div className="mt-2 space-y-1 text-xs text-slate-600" data-testid="ms-access-linked">
          <StatusBadge tone="green">Collegato</StatusBadge>
          <p>I tuoi utenti entrano con il loro account Microsoft dell'azienda; calendario Outlook e invio email dalla loro casella si collegano da soli. Dopo il primo accesso con Microsoft la password non vale più.</p>
          <p className="text-slate-400">Collegato da {s.linked_by} · {s.microsoft_users} utenti con Microsoft</p></div>
        : <div className="mt-2 space-y-3 text-xs text-slate-600">
          <p>Entra una volta con il tuo account Microsoft aziendale (la stessa email del tuo utente): da quel momento i colleghi già creati in ON! Suite potranno accedere con Microsoft, con calendario e posta Outlook. La tua password non varrà più. Se Microsoft chiede l'approvazione dell'amministratore, il vostro amministratore Microsoft dovrà approvare ON! Suite.</p>
          <Button asChild size="sm" data-testid="ms-access-link"><a href={LINK_URL}>Collega Microsoft 365</a></Button></div>}
    </Panel>
  );
};

export const PlatformMicrosoftCard = () => {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/platform/microsoft").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!s) return null;
  const set = async (v) => {
    if (v && !window.confirm("Hai già impostato l'app ON! Suite su «Account in qualsiasi directory organizzativa» nel portale Azure? Senza quel passaggio l'accesso Microsoft smette di funzionare anche per Gruppo ON!.")) return;
    try { await api.put("/platform/microsoft", { ms_multitenant: v }); toast.success(v ? "Accesso Microsoft aperto alle aziende" : "Accesso Microsoft solo per Gruppo ON!"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Panel className="mb-4 flex flex-wrap items-start gap-4 p-4" data-testid="platform-ms-card">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink">Accesso Microsoft per le aziende clienti</p>
        <p className="text-xs text-slate-500">Prima rendi multi-tenant l'app nel portale Azure: Registrazioni app → ON! Suite → Autenticazione → «Account in qualsiasi directory organizzativa». Poi attiva qui. {s.linked_companies.length ? `Collegate: ${s.linked_companies.join(", ")}.` : ""}</p>
      </div>
      <Switch checked={s.ms_multitenant} onCheckedChange={set} data-testid="platform-ms-toggle" />
    </Panel>
  );
};
